/**
 * Paste cleanup for the rich-text editor (Word, Outlook, web pages).
 *
 * Runs before ProseMirror parses the pasted HTML (`transformPastedHTML`). The editor schema then
 * drops whatever is left that it does not know. This step handles what the schema cannot:
 * - Office markup: conditional comments, <o:p>/<v:…> elements, `mso-*` styles, class names;
 * - Word lists, which arrive as paragraphs (`mso-list: l0 level2`) with a fake bullet span;
 * - colors that only restate the default (black/windowtext text, white background), which would
 *   make pasted text invisible in dark mode and noisy in the HTML;
 * - images that are not embedded (remote http(s) and local file:// sources): mail must not load
 *   external resources, so only data: images stay (they become inline attachments on send).
 */

const DROP_ELEMENTS = 'style, script, meta, link, title, xml, head, template, noscript, iframe, object, embed';
const DEFAULT_COLORS = /^(black|windowtext|#000|#000000|rgb\(0,\s*0,\s*0\))$/i;
const DEFAULT_BACKGROUNDS = /^(white|window|transparent|#fff|#ffffff|rgb\(255,\s*255,\s*255\))$/i;
const ORDERED_MARKER = /^\s*(\d+|[a-z]|[ivxlcdm]+)[.)]\s*$/i;

function removeComments(root: Node): void {
  const walker = root.ownerDocument?.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
  const comments: Node[] = [];
  while (walker?.nextNode()) comments.push(walker.currentNode);
  for (const comment of comments) comment.parentNode?.removeChild(comment);
}

function unwrap(element: Element): void {
  element.replaceWith(...Array.from(element.childNodes));
}

/** Keeps only meaningful, non-Office inline styles. */
function cleanStyle(element: HTMLElement): void {
  const style = element.style;
  for (const property of Array.from(style)) {
    if (property.startsWith('mso-')) style.removeProperty(property);
  }
  if (DEFAULT_COLORS.test(style.color.trim())) style.removeProperty('color');
  if (DEFAULT_BACKGROUNDS.test(style.backgroundColor.trim())) style.removeProperty('background-color');
  if (element.getAttribute('style')?.trim() === '') element.removeAttribute('style');
}

/** Word list paragraph: `mso-list: l0 level2 lfo1` → level 2, or undefined. */
function wordListLevel(element: Element): number | undefined {
  const style = element.getAttribute('style') ?? '';
  const match = /mso-list:\s*l\d+\s+level(\d+)/i.exec(style);
  if (match?.[1]) return Number(match[1]);
  return /^MsoListParagraph/i.test(element.getAttribute('class') ?? '') ? 1 : undefined;
}

/** Removes Word's fake bullet (`<span style="mso-list:Ignore">1.<span> </span></span>`) and returns its text. */
function takeWordMarker(paragraph: Element): string {
  const marker = Array.from(paragraph.querySelectorAll('span')).find((span) => /mso-list:\s*ignore/i.test(span.getAttribute('style') ?? ''));
  if (!marker) return '';
  const text = marker.textContent ?? '';
  marker.remove();
  return text;
}

/** Turns runs of Word list paragraphs into nested <ul>/<ol>. */
function convertWordLists(root: ParentNode, doc: Document): void {
  const paragraphs = Array.from(root.querySelectorAll('p')).filter((p) => wordListLevel(p) !== undefined);
  let stack: HTMLElement[] = [];
  let previous: Element | undefined;
  for (const paragraph of paragraphs) {
    const level = wordListLevel(paragraph) ?? 1;
    const ordered = ORDERED_MARKER.test(takeWordMarker(paragraph).replace(/\u00a0/g, ' '));
    // A new run starts when this paragraph does not directly follow the previous list paragraph.
    if (previous === undefined || paragraph.previousElementSibling !== previous) stack = [];
    while (stack.length > level) stack.pop();
    while (stack.length < level) {
      const list = doc.createElement(ordered ? 'ol' : 'ul');
      const parentItem = stack.at(-1)?.lastElementChild;
      if (parentItem) parentItem.append(list);
      else paragraph.before(list);
      stack.push(list);
    }
    const item = doc.createElement('li');
    const current = stack.at(-1);
    current?.append(item);
    previous = paragraph;
    // Keep the paragraph as a placeholder sibling until the run ends, so adjacency checks work.
    const content = doc.createElement('p');
    content.append(...Array.from(paragraph.childNodes));
    item.append(content);
    paragraph.setAttribute('data-word-list-done', '');
  }
  for (const done of Array.from(root.querySelectorAll('p[data-word-list-done]'))) done.remove();
}

function keepImage(image: HTMLImageElement): boolean {
  return /^data:image\/(png|jpeg|gif|webp);base64,/i.test(image.getAttribute('src') ?? '');
}

/** Cleans pasted HTML (see module comment). Pure function on a detached document. */
export function cleanPastedHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const body = doc.body;
  removeComments(body);
  for (const element of Array.from(body.querySelectorAll(DROP_ELEMENTS))) element.remove();
  convertWordLists(body, doc);
  for (const element of Array.from(body.querySelectorAll('*'))) {
    // Office namespaces (<o:p>, <v:shape>, <w:…>): keep the text only.
    if (element.tagName.includes(':')) {
      if (/^v:/i.test(element.tagName)) element.remove();
      else unwrap(element);
      continue;
    }
    if (element instanceof HTMLImageElement && !keepImage(element)) {
      element.remove();
      continue;
    }
    if (element instanceof HTMLAnchorElement && !element.hasAttribute('href')) {
      unwrap(element);
      continue;
    }
    element.removeAttribute('class');
    element.removeAttribute('id');
    if (element instanceof HTMLElement) cleanStyle(element);
  }
  return body.innerHTML;
}
